import type { ChangeEvent, ReactNode } from "react";

const BASE_INPUT =
  "w-full rounded-md border bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-400";

function borderClass(error?: string) {
  return error ? "border-primary-400" : "border-background-300";
}

interface FieldShellProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}

function FieldShell({ label, htmlFor, required, hint, error, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-xs font-semibold text-foreground-700">
        {label}
        {required ? <span className="ml-0.5 text-primary-500">*</span> : null}
      </label>
      {children}
      {error ? (
        <span className="text-xs text-primary-600">{error}</span>
      ) : hint ? (
        <span className="text-xs text-foreground-400">{hint}</span>
      ) : null}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  maxLength?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  inputMode?: "text" | "numeric" | "tel" | "email";
}

export function TextField({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  error,
  hint,
  disabled,
  maxLength,
  autoComplete,
  autoFocus,
  inputMode,
}: TextFieldProps) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} hint={hint} error={error}>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        inputMode={inputMode}
        className={`${BASE_INPUT} ${borderClass(error)}`}
      />
    </FieldShell>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
}

export function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  required,
  error,
  hint,
  disabled,
}: SelectFieldProps) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} hint={hint} error={error}>
      <select
        id={name}
        name={name}
        value={value}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
        className={`${BASE_INPUT} ${borderClass(error)} appearance-none`}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

interface TextAreaFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  hint?: string;
  required?: boolean;
  error?: string;
}

export function TextAreaField({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  hint,
  required,
  error,
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} hint={hint} error={error}>
      <textarea
        id={name}
        name={name}
        value={value}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
        className={`${BASE_INPUT} ${borderClass(error)} resize-none`}
      />
    </FieldShell>
  );
}

interface ToggleSwitchProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function ToggleSwitch({ label, description, checked, onChange }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-left transition-colors hover:bg-background-100"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground-900">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-xs text-foreground-500">{description}</span>
        ) : null}
      </span>
      <span
        className={`relative flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-accent-500" : "bg-background-300"
        }`}
      >
        <span
          className={`absolute h-5 w-5 rounded-full bg-background-50 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}